import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  initialItemSelectionState,
  itemSelectionReducer,
  type ItemSelectionState,
} from "../lib/itemSelectionMode.js";

const LONG_PRESS_MS = 500;
const MOVE_CANCEL_PX = 8;

export type ItemSelection = {
  selectedIds: ReadonlySet<string>;
  /** True while the user has explicitly entered multi-select mode. */
  isMultiSelectMode: boolean;
  isSelected: (id: string) => boolean;
  /** Turns the mode on without selecting anything. */
  enterMode: () => void;
  /** Turns the mode off and drops the selection and anchor. The single exit. */
  exitMode: () => void;
  /** Drops the selection but stays in the mode - used after a completed bulk action. */
  clearSelection: () => void;
  toggleOne: (id: string) => void;
  selectMany: (ids: readonly string[]) => void;
  deselectMany: (ids: readonly string[]) => void;
  anchorId: string | null;
  setAnchor: (id: string | null) => void;
  /** Spread onto the row element. Fires selection on a 500ms hold from a non-mouse pointer. */
  longPressHandlers: (id: string) => {
    onPointerDown: (e: ReactPointerEvent) => void;
    onPointerMove: (e: ReactPointerEvent) => void;
    onPointerUp: (e: ReactPointerEvent) => void;
    onPointerCancel: (e: ReactPointerEvent) => void;
  };
  /** True for exactly one click event immediately after a long-press fired. */
  consumeSuppressedClick: () => boolean;
};

export function useItemSelection(): ItemSelection {
  const [state, dispatch] = useReducer(itemSelectionReducer, initialItemSelectionState);
  const { mode, selectedIds, anchorId }: ItemSelectionState = state;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originRef = useRef<{ id: string; x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    originRef.current = null;
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const enterMode = useCallback(() => {
    dispatch({ type: "enterMode" });
  }, []);

  const exitMode = useCallback(() => {
    dispatch({ type: "exitMode" });
  }, []);

  const clearSelection = useCallback(() => {
    dispatch({ type: "clearSelection" });
  }, []);

  const toggleOne = useCallback((id: string) => {
    dispatch({ type: "toggleOne", id });
  }, []);

  const selectMany = useCallback((ids: readonly string[]) => {
    dispatch({ type: "selectMany", ids });
  }, []);

  const deselectMany = useCallback((ids: readonly string[]) => {
    dispatch({ type: "deselectMany", ids });
  }, []);

  const setAnchor = useCallback((id: string | null) => {
    dispatch({ type: "setAnchor", id });
  }, []);

  const longPressHandlers = useCallback(
    (id: string) => ({
      onPointerDown: (e: ReactPointerEvent) => {
        if (e.pointerType === "mouse") return;
        originRef.current = { id, x: e.clientX, y: e.clientY };
        if (timerRef.current !== null) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          originRef.current = null;
          dispatch({ type: "longPress", id });
          suppressClickRef.current = true;
          if (typeof navigator !== "undefined") navigator.vibrate?.(10);
        }, LONG_PRESS_MS);
      },
      onPointerMove: (e: ReactPointerEvent) => {
        const origin = originRef.current;
        if (!origin || origin.id !== id) return;
        const dx = e.clientX - origin.x;
        const dy = e.clientY - origin.y;
        if (Math.sqrt(dx * dx + dy * dy) > MOVE_CANCEL_PX) clearTimer();
      },
      onPointerUp: () => clearTimer(),
      onPointerCancel: () => clearTimer(),
    }),
    [clearTimer]
  );

  const consumeSuppressedClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  return {
    selectedIds,
    isMultiSelectMode: mode,
    isSelected,
    enterMode,
    exitMode,
    clearSelection,
    toggleOne,
    selectMany,
    deselectMany,
    anchorId,
    setAnchor,
    longPressHandlers,
    consumeSuppressedClick,
  };
}
